import { useQuery } from "@tanstack/react-query";
import {
  AlertTriangle,
  ArrowDownRight,
  ArrowUpRight,
  Banknote,
  CalendarDays,
  ChartLine,
  CircleDollarSign,
  Megaphone,
  Package,
  Receipt,
  Store,
  Truck,
  TrendingUp,
  UserCheck,
  Download,
  PackageOpen,
} from "lucide-react";
import { useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { KpiCard } from "@/components/ui/KpiCard";
import { PageHeader } from "@/components/ui/PageHeader";
import { MultiSelect } from "@/components/ui/MultiSelect";
import { api } from "@/lib/api";
import clsx from "clsx";

type Item = {
  variation_id: string;
  product_id: number | null;
  name: string;
  total_qty: number;
  n_orders: number;
  n_returns: number;
  return_rate: number;
  revenue: number;
  cost_price: number;
  cogs: number;
  ship_cost: number;
  marketing_alloc: number;
  gross_profit: number;
  net_profit: number;
  gross_margin_pct: number;
  net_margin_pct: number;
  has_cost: boolean;
};

type ShopBreakdown = {
  shop_id: number;
  shop_name: string;
  shop_code: string;
  n_orders: number;
  revenue: number;
  ship_cost: number;
};

type PnlResult = {
  date_from: string;
  date_to: string;
  shop_id: number | null;
  total_revenue: number;
  total_cogs: number;
  total_ship_cost: number;
  total_marketing: number;
  total_gross_profit: number;
  total_net_profit: number;
  n_products_tracked: number;
  n_products_missing_cost: number;
  items: Item[];
  by_shop: ShopBreakdown[];
};

type MonthlyDay = {
  date: string;
  n_total: number;
  n_shipped: number;
  n_received: number;
  n_returning: number;
  n_returned: number;
  ship_cost: number;
  revenue: number;
  cod_total: number;
  returned_value: number;
  marketing_cost: number;       // đã cộng thuế NT 10%
  marketing_cost_raw: number;    // chưa cộng thuế
  return_rate: number;
  n_partial: number;             // đơn giao một phần (khách chỉ trả ship)
  partial_cod_gross: number;     // COD GỐC của chúng — không phải tiền thực thu
};

type OrderDetail = {
  pos_order_id: string;
  partner_extend_code: string | null;
  partner_name: string | null;
  date: string | null;
  status: number;
  status_name: string | null;
  cod: number;
  partner_fee: number;
  customer: string | null;
  items: string | null;
  shop_name: string;
  is_partial: boolean;
  cod_gross: number;   // COD gốc trên đơn — chỉ để đối chiếu
};

type MonthlyResult = {
  year: number;
  month: number;
  shop_id: number | null;
  marketing_source: "ads_live" | "invoices";
  days: MonthlyDay[];
  totals: MonthlyDay;
  order_details: OrderDetail[];
  partial_delivery_cod: number;
  suggested_month?: { year: number; month: number; n_orders: number };
};

type VariationOpt = {
  variation_id: string;
  detail: string;
  n_orders: number;
  qty: number;
};

type ProductOpt = {
  name: string;
  n_orders: number;
  qty: number;
  variations: VariationOpt[];
};

type ProductCodeLite = {
  id: number;
  code: string;
  name: string;
  n_shops: number;
};

const fmt = (v: number) => Math.round(v).toLocaleString("vi-VN") + "đ";
const fmtN = (v: number) =>
  v === 0 ? "—" : Math.round(v).toLocaleString("vi-VN");
const fmtNumOnly = (v: number) => v.toLocaleString("vi-VN");
const fmtShort = (v: number) => {
  const abs = Math.abs(v);
  if (abs >= 1_000_000_000) return (v / 1_000_000_000).toFixed(1) + "B";
  if (abs >= 1_000_000) return (v / 1_000_000).toFixed(1) + "M";
  if (abs >= 1_000) return (v / 1_000).toFixed(0) + "K";
  return Math.round(v).toString();
};

const now = new Date();
const startOfYear = () => now.getFullYear() + "-01-01";
const today = () => now.toISOString().slice(0, 10);

const STATUS_MAP: Record<number, { label: string; tone: string }> = {
  0: { label: "Mới", tone: "badge-neutral" },
  1: { label: "Đã xác nhận", tone: "badge-brand" },
  2: { label: "Đã gửi hàng", tone: "badge-brand" },
  3: { label: "Đã nhận", tone: "badge-success" },
  4: { label: "Đang hoàn", tone: "badge-warning" },
  5: { label: "Đã hoàn", tone: "badge-danger" },
  6: { label: "Đã huỷ", tone: "badge-danger" },
  7: { label: "Đã xoá", tone: "badge-danger" },
  8: { label: "Đang đóng", tone: "badge-neutral" },
  9: { label: "Chờ chuyển", tone: "badge-neutral" },
  11: { label: "Chờ hàng", tone: "badge-neutral" },
  12: { label: "Chờ in", tone: "badge-neutral" },
  13: { label: "Đã in", tone: "badge-neutral" },
  16: { label: "Đã thu tiền", tone: "badge-success" },
  17: { label: "Chờ xác nhận", tone: "badge-neutral" },
  20: { label: "Đã đặt", tone: "badge-neutral" },
};

type StaffRow = {
  editor_id: string | null;
  name: string;
  n_confirmed: number;
  n_days_active: number;
  avg_per_active_day: number;
  peak_hour: number | null;
  by_hour: number[];
  earliest_time: string;
  latest_time: string;
};

type StaffResult = {
  date_from: string;
  date_to: string;
  totals: {
    n_confirmed: number;
    n_staff: number;
    n_days: number;
    avg_per_day: number;
  };
  staff: StaffRow[];
  staff_names: string[];
  days: { date: string; total: number; per_staff: Record<string, number> }[];
  hours: { hour: number; n: number }[];
};

type SortKey =
  | "revenue"
  | "net_profit"
  | "gross_profit"
  | "net_margin_pct"
  | "return_rate"
  | "total_qty";

type Tab = "monthly" | "pnl" | "orders" | "staff";

const CARRIER_OPTIONS = ["VTP", "Shopee Xpress", "J&T", "Best Inc"];

export function ProductPnlPage() {
  const [tab, setTab] = useState<Tab>("monthly");
  const [codeId, setCodeId] = useState<number | "">("");
  const [carrier, setCarrier] = useState<string>("");
  // Lựa chọn thô trên UI (chọn nhiều)
  const [selProductNames, setSelProductNames] = useState<string[]>([]);
  const [selVariationIds, setSelVariationIds] = useState<string[]>([]);
  const [month, setMonth] = useState<string>(
    `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`
  );
  const [year, monthStr] = month.split("-");

  // For PnL tab
  const [dateFrom, setDateFrom] = useState(startOfYear());
  const [dateTo, setDateTo] = useState(today());
  const [sortKey, setSortKey] = useState<SortKey>("net_profit");
  const [sortDesc, setSortDesc] = useState(true);

  const codesQ = useQuery({
    queryKey: ["product-codes"],
    queryFn: async () =>
      (await api.get<ProductCodeLite[]>("/product-codes")).data,
  });

  const prodOptsQ = useQuery({
    queryKey: ["product-options"],
    queryFn: async () =>
      (await api.get<{ products: ProductOpt[] }>("/reports/product-options"))
        .data.products,
  });
  const prodByName = useMemo(
    () => new Map((prodOptsQ.data ?? []).map((p) => [p.name, p])),
    [prodOptsQ.data]
  );
  // Phân loại của các SP đang chọn, gom nhóm theo tên SP
  const variationOptions = selProductNames.flatMap((n) =>
    (prodByName.get(n)?.variations ?? []).map((v) => ({
      value: v.variation_id,
      label: v.detail || "(không phân loại)",
      count: v.n_orders,
      group: n,
    }))
  );
  // Bộ lọc gửi lên API: SP đã chọn phân loại riêng → chỉ lọc các phân loại đó,
  // SP còn lại → lấy mọi phân loại (gửi theo tên SP).
  const variationIds = selVariationIds;
  const productNames = selProductNames.filter(
    (n) =>
      !(prodByName.get(n)?.variations ?? []).some((v) =>
        selVariationIds.includes(v.variation_id)
      )
  );
  const changeProducts = (names: string[]) => {
    setSelProductNames(names);
    // bỏ các phân loại thuộc SP vừa bỏ chọn
    const keep = new Set(
      names.flatMap((n) =>
        (prodByName.get(n)?.variations ?? []).map((v) => v.variation_id)
      )
    );
    setSelVariationIds((ids) => ids.filter((id) => keep.has(id)));
  };

  const [exporting, setExporting] = useState(false);

  // Xuất TẤT CẢ dữ liệu của trang ra 1 file .xlsx (5 sheet), giữ nguyên bộ lọc
  // đang chọn. Phải tải qua axios (kèm Bearer token) rồi tạo blob — thẻ <a>
  // trỏ thẳng vào API sẽ bị 401 vì trình duyệt không gắn token.
  const exportXlsx = async () => {
    setExporting(true);
    try {
      const r = await api.get("/reports/business-tracking.xlsx", {
        paramsSerializer: { indexes: null },
        params: {
          year: parseInt(year),
          month: parseInt(monthStr),
          date_from: dateFrom,
          date_to: dateTo,
          ...(codeId ? { product_code_id: codeId } : {}),
          ...(carrier ? { carrier } : {}),
          ...(productNames.length ? { product_name: productNames } : {}),
          ...(variationIds.length ? { variation_id: variationIds } : {}),
        },
        responseType: "blob",
      });
      const blob = new Blob([r.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `TheoDoiKinhDoanh_${monthStr}-${year}.xlsx`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
    } catch {
      alert("Xuất Excel thất bại. Thử lại hoặc thu hẹp khoảng ngày.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="p-6 max-w-[1600px] mx-auto">
      <PageHeader
        title="Theo dõi kinh doanh"
        subtitle="Thống kê đơn hàng theo shop + tháng · lãi lỗ per SP · chi tiết đơn"
        actions={
          <button
            onClick={exportXlsx}
            disabled={exporting}
            className="btn-primary flex items-center gap-2 disabled:opacity-60"
            title="Tải tất cả 4 tab về 1 file Excel 5 sheet"
          >
            <Download size={16} />
            {exporting ? "Đang xuất…" : "Xuất Excel"}
          </button>
        }
        icon={CircleDollarSign}
      />

      {/* Mã hàng + period selector — shared across tabs */}
      <Card className="mb-4">
        <CardBody className="flex flex-wrap items-end gap-3">
          <div>
            <label className="label">Mã hàng (gộp N shop)</label>
            <select
              value={codeId}
              onChange={(e) => {
                setCodeId(e.target.value ? Number(e.target.value) : "");
                // DS sản phẩm lọc theo mã hàng → SP đang chọn có thể không còn
                setSelProductNames([]);
                setSelVariationIds([]);
              }}
              className="input w-72"
            >
              <option value="">— Tất cả mã hàng —</option>
              {codesQ.data?.map((c) => (
                <option key={c.id} value={c.id}>
                  [{c.code}] {c.name} ({c.n_shops} shop)
                </option>
              ))}
            </select>
            {codesQ.data?.length === 0 && (
              <a
                href="/ma-hang"
                className="text-[10px] text-brand-600 underline mt-1 inline-block"
              >
                Chưa có mã hàng — tạo tại /ma-hang
              </a>
            )}
          </div>
          {tab === "monthly" || tab === "orders" ? (
            <>
              <div>
                <label className="label">Tháng</label>
                <input
                  type="month"
                  value={month}
                  onChange={(e) => setMonth(e.target.value)}
                  className="input w-40"
                />
              </div>
              <div>
                <label className="label">ĐVVC</label>
                <select
                  value={carrier}
                  onChange={(e) => setCarrier(e.target.value)}
                  className="input w-40"
                >
                  <option value="">— Tất cả —</option>
                  {CARRIER_OPTIONS.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label">Sản phẩm</label>
                <MultiSelect
                  options={(prodOptsQ.data ?? []).map((pr) => ({
                    value: pr.name,
                    label: pr.name,
                    count: pr.n_orders,
                  }))}
                  value={selProductNames}
                  onChange={changeProducts}
                  placeholder="— Tất cả sản phẩm —"
                  unit="sản phẩm"
                  loading={prodOptsQ.isLoading}
                  className="w-64"
                />
              </div>
              <div>
                <label className="label">Phân loại</label>
                <MultiSelect
                  options={variationOptions}
                  value={selVariationIds}
                  onChange={setSelVariationIds}
                  placeholder="— Tất cả phân loại —"
                  unit="phân loại"
                  disabled={selProductNames.length === 0}
                  disabledText="— Chọn sản phẩm trước —"
                  className="w-56"
                />
              </div>
              {selProductNames.length > 0 && (
                <button
                  onClick={() => {
                    setSelProductNames([]);
                    setSelVariationIds([]);
                  }}
                  className="btn-ghost text-xs mb-1.5"
                >
                  ✕ Bỏ lọc SP
                </button>
              )}
            </>
          ) : (
            <>
              <div>
                <label className="label">Từ ngày</label>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="input w-40"
                />
              </div>
              <div>
                <label className="label">Đến ngày</label>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="input w-40"
                />
              </div>
            </>
          )}
        </CardBody>
      </Card>

      {/* Tabs */}
      <div className="border-b border-slate-200 mb-4 flex gap-1">
        <TabBtn
          active={tab === "monthly"}
          onClick={() => setTab("monthly")}
          icon={CalendarDays}
        >
          Thống kê tháng
        </TabBtn>
        <TabBtn
          active={tab === "pnl"}
          onClick={() => setTab("pnl")}
          icon={ChartLine}
        >
          Lãi lỗ SP
        </TabBtn>
        <TabBtn
          active={tab === "orders"}
          onClick={() => setTab("orders")}
          icon={Receipt}
        >
          Chi tiết đơn
        </TabBtn>
        <TabBtn
          active={tab === "staff"}
          onClick={() => setTab("staff")}
          icon={UserCheck}
        >
          Hiệu suất NV
        </TabBtn>
      </div>

      {tab === "monthly" && (
        <MonthlyTab
          codeId={codeId || null}
          carrier={carrier || null}
          productNames={productNames}
          variationIds={variationIds}
          year={parseInt(year)}
          month={parseInt(monthStr)}
          onGoToMonth={(y, m) =>
            setMonth(`${y}-${String(m).padStart(2, "0")}`)
          }
        />
      )}
      {tab === "pnl" && (
        <PnlTab
          codeId={codeId || null}
          dateFrom={dateFrom}
          dateTo={dateTo}
          sortKey={sortKey}
          setSortKey={setSortKey}
          sortDesc={sortDesc}
          setSortDesc={setSortDesc}
        />
      )}
      {tab === "orders" && (
        <OrdersTab
          codeId={codeId || null}
          carrier={carrier || null}
          productNames={productNames}
          variationIds={variationIds}
          year={parseInt(year)}
          month={parseInt(monthStr)}
        />
      )}
      {tab === "staff" && <StaffTab dateFrom={dateFrom} dateTo={dateTo} />}
    </div>
  );
}

function TabBtn({
  active,
  onClick,
  icon: Icon,
  children,
}: {
  active: boolean;
  onClick: () => void;
  icon: any;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={clsx(
        "px-4 py-2 text-sm font-medium border-b-2 -mb-px flex items-center gap-1.5 transition-colors",
        active
          ? "border-brand-600 text-brand-700"
          : "border-transparent text-slate-500 hover:text-slate-900"
      )}
    >
      <Icon size={14} />
      {children}
    </button>
  );
}

// ═════════════════════════════════════════════════════════════
// TAB 1: Monthly stats (Google Sheets style)
// ═════════════════════════════════════════════════════════════

function MonthlyTab({
  codeId,
  carrier,
  productNames,
  variationIds,
  year,
  month,
  onGoToMonth,
}: {
  codeId: number | null;
  carrier: string | null;
  productNames: string[];
  variationIds: string[];
  year: number;
  month: number;
  onGoToMonth: (year: number, month: number) => void;
}) {
  const q = useQuery({
    queryKey: [
      "shop-monthly", codeId, carrier, year, month, productNames, variationIds,
    ],
    queryFn: async () =>
      (
        await api.get<MonthlyResult>("/reports/shop-monthly", {
          paramsSerializer: { indexes: null },
          params: {
            year,
            month,
            ...(codeId ? { product_code_id: codeId } : {}),
            ...(carrier ? { carrier } : {}),
            ...(productNames.length ? { product_name: productNames } : {}),
            ...(variationIds.length ? { variation_id: variationIds } : {}),
          },
        })
      ).data,
  });

  if (q.isLoading) {
    return <div className="text-slate-500 py-8 text-center">Đang tải...</div>;
  }
  if (!q.data) return null;
  const { days, totals } = q.data;
  const suggested = q.data.suggested_month;

  return (
    <>
      {days.length === 0 && suggested && (
        <div className="mb-4 bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm flex items-center justify-between">
          <div className="text-amber-900">
            <b>Không có đơn</b> nào được đẩy trong tháng{" "}
            {String(month).padStart(2, "0")}/{year}. Có{" "}
            <b>{suggested.n_orders} đơn</b> trong tháng{" "}
            <b>{String(suggested.month).padStart(2, "0")}/{suggested.year}</b>.
          </div>
          <button
            onClick={() => onGoToMonth(suggested.year, suggested.month)}
            className="btn-primary text-xs"
          >
            → Chuyển sang tháng {String(suggested.month).padStart(2, "0")}/
            {suggested.year}
          </button>
        </div>
      )}

      {/* KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 mb-4">
        <KpiCard
          label="Tổng đơn"
          value={fmtNumOnly(totals.n_total)}
          hint="đi hàng trong tháng"
          icon={Receipt}
          tone="brand"
        />
        <KpiCard
          label="Đã gửi hàng"
          value={fmtNumOnly(totals.n_shipped)}
          icon={Truck}
          tone="slate"
        />
        <KpiCard
          label="Đã nhận"
          value={fmtNumOnly(totals.n_received)}
          icon={TrendingUp}
          tone="green"
        />
        <KpiCard
          label="Hoàn"
          value={fmtNumOnly(
            totals.n_returning + totals.n_returned + totals.n_partial
          )}
          hint={`${(totals.return_rate * 100).toFixed(1)}% tỷ lệ hoàn · ${fmtNumOnly(
            totals.n_returning
          )} đang + ${fmtNumOnly(totals.n_returned)} đã + ${fmtNumOnly(
            totals.n_partial
          )} một phần`}
          icon={ArrowDownRight}
          tone={totals.return_rate > 0.1 ? "red" : "amber"}
        />
        <KpiCard
          label="Hoàn 1 phần"
          value={fmtNumOnly(totals.n_partial)}
          hint={
            totals.n_partial > 0
              ? `tính ${fmt(q.data.partial_delivery_cod)}/đơn · COD gốc ${fmtShort(
                  totals.partial_cod_gross
                )}`
              : "khách chỉ trả ship, hàng hoàn về"
          }
          icon={PackageOpen}
          tone={totals.n_partial > 0 ? "amber" : "slate"}
        />
        <KpiCard
          label="Doanh thu"
          value={fmtShort(totals.revenue)}
          hint="đơn đã nhận"
          icon={Banknote}
          tone="green"
        />
        <KpiCard
          label="Phí ship"
          value={fmtShort(totals.ship_cost)}
          hint="từ bảng kê VTP"
          icon={Truck}
          tone="slate"
        />
        <KpiCard
          label="Chi phí QC"
          value={fmtShort(totals.marketing_cost)}
          hint={
            q.data.marketing_source === "ads_live"
              ? `Meta live · +10% thuế NT`
              : "Từ hoá đơn"
          }
          icon={Megaphone}
          tone="amber"
        />
      </div>

      {/* Daily table (Google Sheets style) */}
      <Card>
        <CardHeader
          title={`Thống kê tháng ${String(month).padStart(2, "0")}/${year}`}
          subtitle={
            q.data.marketing_source === "ads_live"
              ? "Mốc ngày = ngày đi hàng (chuyển sang Đã gửi hàng). Phí ship từ bảng kê VTP. Chi phí QC live từ Meta ads (đã cộng thuế NT 10%, số trong ngoặc chưa cộng). Tỷ lệ hoàn = (đang hoàn + đã hoàn + hoàn 1 phần) / tổng đơn. Hoàn 1 phần = khách chỉ trả tiền ship, hàng hoàn về — doanh thu tính 20.000đ/đơn, chi phí gồm cả phí giao + phí hoàn."
              : "Mốc ngày = ngày đi hàng (chuyển sang Đã gửi hàng). Phí ship từ bảng kê VTP. Chi phí QC phân bổ theo tỉ trọng doanh thu từ hoá đơn. Tỷ lệ hoàn = (đang hoàn + đã hoàn + hoàn 1 phần) / tổng đơn. Hoàn 1 phần = khách chỉ trả tiền ship, hàng hoàn về — doanh thu tính 20.000đ/đơn, chi phí gồm cả phí giao + phí hoàn."
          }
        />
        <div className="overflow-x-auto scrollbar-thin">
          <table className="table-modern">
            <thead>
              <tr>
                <th className="min-w-[100px]">Ngày</th>
                <th className="text-right">Tổng đơn</th>
                <th className="text-right">Đã gửi</th>
                <th className="text-right">Đã nhận</th>
                <th className="text-right">Đang hoàn</th>
                <th className="text-right">Đã hoàn</th>
                <th className="text-right">Hoàn 1 phần</th>
                <th className="text-right">Phí ship</th>
                <th className="text-right">Chi phí QC</th>
                <th className="text-right">Doanh thu</th>
                <th className="text-right">Tiền COD</th>
                <th className="text-right">Giá trị hoàn</th>
                <th className="text-right">Tỷ lệ hoàn</th>
              </tr>
            </thead>
            <tbody>
              {days.length === 0 && (
                <tr>
                  <td colSpan={13} className="text-center py-8 text-slate-500">
                    Không có đơn trong tháng này
                  </td>
                </tr>
              )}
              {days.map((d) => (
                <tr key={d.date}>
                  <td className="font-medium">
                    {new Date(d.date + "T00:00:00").toLocaleDateString("vi-VN")}
                  </td>
                  <td className="text-right font-mono">{fmtN(d.n_total)}</td>
                  <td className="text-right font-mono text-slate-600">
                    {fmtN(d.n_shipped)}
                  </td>
                  <td className="text-right font-mono text-emerald-700 font-semibold">
                    {fmtN(d.n_received)}
                  </td>
                  <td className="text-right font-mono text-amber-700">
                    {fmtN(d.n_returning)}
                  </td>
                  <td className="text-right font-mono text-red-600">
                    {fmtN(d.n_returned)}
                  </td>
                  <td
                    className="text-right font-mono text-orange-600"
                    title="Khách chỉ trả tiền ship, hàng hoàn về — doanh thu thực chưa xác định"
                  >
                    {fmtN(d.n_partial)}
                  </td>
                  <td className="text-right font-mono text-slate-600">
                    {d.ship_cost > 0 ? fmt(d.ship_cost) : "—"}
                  </td>
                  <td className="text-right font-mono text-amber-700">
                    {d.marketing_cost > 0 ? (
                      <>
                        <div>{fmt(d.marketing_cost)}</div>
                        {d.marketing_cost_raw > 0 &&
                          Math.abs(d.marketing_cost - d.marketing_cost_raw) > 1 && (
                            <div
                              className="text-[9px] text-slate-400"
                              title="Chưa cộng thuế nhà thầu 10%"
                            >
                              ({fmt(d.marketing_cost_raw)})
                            </div>
                          )}
                      </>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="text-right font-mono font-semibold text-emerald-700">
                    {d.revenue > 0 ? fmt(d.revenue) : "—"}
                  </td>
                  <td className="text-right font-mono text-slate-600">
                    {d.cod_total > 0 ? fmt(d.cod_total) : "—"}
                  </td>
                  <td className="text-right font-mono text-red-600">
                    {d.returned_value > 0 ? fmt(d.returned_value) : "—"}
                  </td>
                  <td
                    className={clsx(
                      "text-right font-mono text-xs",
                      d.return_rate > 0.1
                        ? "text-red-600 font-semibold"
                        : "text-slate-500"
                    )}
                  >
                    {(d.return_rate * 100).toFixed(1)}%
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-slate-200 bg-slate-50 font-semibold">
                <td className="text-slate-900">TỔNG</td>
                <td className="text-right font-mono">
                  {fmtN(totals.n_total)}
                </td>
                <td className="text-right font-mono">
                  {fmtN(totals.n_shipped)}
                </td>
                <td className="text-right font-mono text-emerald-700">
                  {fmtN(totals.n_received)}
                </td>
                <td className="text-right font-mono text-amber-700">
                  {fmtN(totals.n_returning)}
                </td>
                <td className="text-right font-mono text-red-600">
                  {fmtN(totals.n_returned)}
                </td>
                <td className="text-right font-mono text-orange-600">
                  {fmtN(totals.n_partial)}
                </td>
                <td className="text-right font-mono">
                  {fmt(totals.ship_cost)}
                </td>
                <td className="text-right font-mono text-amber-700">
                  <div>{fmt(totals.marketing_cost || 0)}</div>
                  {totals.marketing_cost_raw > 0 &&
                    Math.abs(totals.marketing_cost - totals.marketing_cost_raw) >
                      1 && (
                      <div
                        className="text-[9px] text-slate-400 font-normal"
                        title="Chưa cộng thuế nhà thầu 10%"
                      >
                        ({fmt(totals.marketing_cost_raw)})
                      </div>
                    )}
                </td>
                <td className="text-right font-mono text-emerald-700">
                  {fmt(totals.revenue)}
                </td>
                <td className="text-right font-mono">{fmt(totals.cod_total)}</td>
                <td className="text-right font-mono text-red-600">
                  {fmt(totals.returned_value)}
                </td>
                <td className="text-right font-mono">
                  {(totals.return_rate * 100).toFixed(1)}%
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </Card>
    </>
  );
}

// ═════════════════════════════════════════════════════════════
// TAB 2: PnL (lãi lỗ per SP — moved existing content here)
// ═════════════════════════════════════════════════════════════

function PnlTab({
  codeId,
  dateFrom,
  dateTo,
  sortKey,
  setSortKey,
  sortDesc,
  setSortDesc,
}: {
  codeId: number | null;
  dateFrom: string;
  dateTo: string;
  sortKey: SortKey;
  setSortKey: (k: SortKey) => void;
  sortDesc: boolean;
  setSortDesc: (b: boolean) => void;
}) {
  const [groupBy, setGroupBy] = useState<"variation" | "product_code">(
    "product_code"
  );

  const q = useQuery({
    queryKey: ["product-pnl", dateFrom, dateTo, codeId, groupBy],
    queryFn: async () =>
      (
        await api.get<PnlResult>("/reports/product-pnl", {
          params: {
            date_from: dateFrom,
            date_to: dateTo,
            group_by: groupBy,
            ...(codeId ? { product_code_id: codeId } : {}),
          },
        })
      ).data,
  });

  const sortedItems = useMemo(() => {
    if (!q.data) return [];
    const arr = [...q.data.items];
    arr.sort((a, b) => {
      const va = a[sortKey] ?? 0;
      const vb = b[sortKey] ?? 0;
      return sortDesc ? vb - va : va - vb;
    });
    return arr;
  }, [q.data, sortKey, sortDesc]);

  const clickSort = (k: SortKey) => {
    if (k === sortKey) setSortDesc(!sortDesc);
    else {
      setSortKey(k);
      setSortDesc(true);
    }
  };

  const arrow = (k: SortKey) => (sortKey === k ? (sortDesc ? " ↓" : " ↑") : "");

  const top10 = useMemo(
    () => sortedItems.slice(0, 10).reverse(),
    [sortedItems]
  );

  if (q.isLoading) {
    return <div className="text-slate-500 py-8 text-center">Đang tính...</div>;
  }
  if (!q.data) return null;

  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
        <KpiCard label="Doanh thu" value={fmtShort(q.data.total_revenue)} icon={Banknote} tone="brand" />
        <KpiCard label="Giá vốn" value={fmtShort(q.data.total_cogs)} icon={Package} tone="slate" />
        <KpiCard label="Phí ship" value={fmtShort(q.data.total_ship_cost)} icon={Truck} tone="slate" />
        <KpiCard label="Marketing" value={fmtShort(q.data.total_marketing)} icon={Megaphone} tone="amber" />
        <KpiCard
          label="Lãi gộp"
          value={fmtShort(q.data.total_gross_profit)}
          icon={q.data.total_gross_profit >= 0 ? ArrowUpRight : ArrowDownRight}
          tone={q.data.total_gross_profit >= 0 ? "green" : "red"}
        />
        <KpiCard
          label="Lãi ròng"
          value={fmtShort(q.data.total_net_profit)}
          hint={
            q.data.total_revenue > 0
              ? `${((q.data.total_net_profit / q.data.total_revenue) * 100).toFixed(1)}% margin`
              : undefined
          }
          icon={TrendingUp}
          tone={q.data.total_net_profit >= 0 ? "green" : "red"}
        />
      </div>

      {q.data.n_products_missing_cost > 0 && (
        <div className="mb-4 flex items-start gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <div>
            <b>{q.data.n_products_missing_cost}</b>/{q.data.n_products_tracked} SP chưa có giá vốn — Lãi lỗ đang PHÓNG ĐẠI.{" "}
            <a href="/san-pham" className="text-brand-700 underline underline-offset-2 font-medium">
              Nhập giá vốn
            </a>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
        <Card className="lg:col-span-2">
          <CardHeader title="Top 10 SP theo lãi ròng" subtitle="Đỏ = lỗ · Xanh = lãi" />
          <CardBody>
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={top10} layout="vertical" margin={{ top: 4, right: 24, left: 0, bottom: 4 }}>
                <CartesianGrid horizontal={false} stroke="#e2e8f0" />
                <XAxis type="number" tickFormatter={fmtShort} tick={{ fill: "#64748b", fontSize: 11 }} axisLine={{ stroke: "#e2e8f0" }} tickLine={false} />
                <YAxis
                  type="category"
                  dataKey="name"
                  width={160}
                  tick={{ fill: "#334155", fontSize: 11 }}
                  axisLine={{ stroke: "#e2e8f0" }}
                  tickLine={false}
                  tickFormatter={(v: string) => (v.length > 22 ? v.slice(0, 22) + "…" : v)}
                />
                <Tooltip
                  cursor={{ fill: "rgba(99,102,241,0.06)" }}
                  contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", boxShadow: "0 4px 12px rgba(0,0,0,0.08)", fontSize: 12 }}
                  formatter={(v: number) => [fmt(v), "Lãi ròng"]}
                />
                <Bar dataKey="net_profit" radius={[0, 4, 4, 0]} barSize={16}>
                  {top10.map((it, i) => (
                    <Cell key={i} fill={it.net_profit >= 0 ? "#10b981" : "#ef4444"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardBody>
        </Card>

        <Card>
          <CardHeader title="Doanh thu theo shop" subtitle={`${q.data.by_shop.length} shop`} />
          <CardBody>
            {q.data.by_shop.length === 0 ? (
              <div className="text-sm text-slate-500 py-8 text-center">Không có đơn</div>
            ) : (
              <div className="space-y-2.5">
                {q.data.by_shop.map((s) => {
                  const maxRev = Math.max(...q.data!.by_shop.map((x) => x.revenue));
                  const pct = maxRev > 0 ? (s.revenue / maxRev) * 100 : 0;
                  return (
                    <div key={s.shop_id}>
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="flex items-center gap-1.5 font-medium text-slate-700">
                          <Store size={12} className="text-brand-600" />
                          {s.shop_name}
                        </span>
                        <span className="text-slate-500 font-mono">{fmtShort(s.revenue)}đ</span>
                      </div>
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full bg-brand-500 rounded-full" style={{ width: pct + "%" }} />
                      </div>
                      <div className="text-[10px] text-slate-500 mt-0.5">
                        {s.n_orders} đơn · ship {fmtShort(s.ship_cost)}đ
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardBody>
        </Card>
      </div>

      <Card>
        <CardHeader
          title="Chi tiết lãi lỗ"
          subtitle={`${sortedItems.length} ${groupBy === "product_code" ? "mã hàng" : "variation"} · click header để sort`}
          action={
            <div className="inline-flex rounded-md border border-slate-200 overflow-hidden text-xs">
              <button
                onClick={() => setGroupBy("product_code")}
                className={clsx(
                  "px-2.5 py-1 transition-colors",
                  groupBy === "product_code"
                    ? "bg-brand-600 text-white"
                    : "bg-white text-slate-600 hover:bg-slate-50"
                )}
              >
                Theo mã hàng
              </button>
              <button
                onClick={() => setGroupBy("variation")}
                className={clsx(
                  "px-2.5 py-1 border-l border-slate-200 transition-colors",
                  groupBy === "variation"
                    ? "bg-brand-600 text-white"
                    : "bg-white text-slate-600 hover:bg-slate-50"
                )}
              >
                Theo variation
              </button>
            </div>
          }
        />
        <div className="overflow-x-auto scrollbar-thin">
          <table className="table-modern">
            <thead>
              <tr>
                <th>Sản phẩm</th>
                <th className="text-right cursor-pointer hover:text-slate-900" onClick={() => clickSort("total_qty")}>SL{arrow("total_qty")}</th>
                <th className="text-right">Đơn</th>
                <th className="text-right cursor-pointer hover:text-slate-900" onClick={() => clickSort("return_rate")}>Hoàn{arrow("return_rate")}</th>
                <th className="text-right cursor-pointer hover:text-slate-900" onClick={() => clickSort("revenue")}>Doanh thu{arrow("revenue")}</th>
                <th className="text-right">Giá vốn/SP</th>
                <th className="text-right">Tổng vốn</th>
                <th className="text-right">Ship</th>
                <th className="text-right">Marketing</th>
                <th className="text-right cursor-pointer hover:text-slate-900" onClick={() => clickSort("gross_profit")}>Lãi gộp{arrow("gross_profit")}</th>
                <th className="text-right cursor-pointer hover:text-slate-900" onClick={() => clickSort("net_profit")}>Lãi ròng{arrow("net_profit")}</th>
                <th className="text-right cursor-pointer hover:text-slate-900" onClick={() => clickSort("net_margin_pct")}>%{arrow("net_margin_pct")}</th>
              </tr>
            </thead>
            <tbody>
              {sortedItems.map((it) => (
                <tr key={it.variation_id} className={it.net_profit < 0 ? "bg-red-50/30" : ""}>
                  <td className="max-w-xs">
                    <div className="font-medium truncate text-slate-900" title={it.name}>{it.name}</div>
                    {!it.has_cost && <span className="badge-danger">chưa có giá vốn</span>}
                  </td>
                  <td className="text-right font-mono">{it.total_qty.toLocaleString("vi-VN")}</td>
                  <td className="text-right font-mono text-slate-500">{it.n_orders}</td>
                  <td className={`text-right font-mono ${it.return_rate > 0.1 ? "text-red-600 font-semibold" : "text-slate-500"}`}>
                    {it.n_returns}·{(it.return_rate * 100).toFixed(0)}%
                  </td>
                  <td className="text-right font-mono font-semibold">{fmt(it.revenue)}</td>
                  <td className="text-right font-mono text-slate-500">{it.cost_price ? fmt(it.cost_price) : "—"}</td>
                  <td className="text-right font-mono text-slate-500">{fmt(it.cogs)}</td>
                  <td className="text-right font-mono text-slate-500">{fmt(it.ship_cost)}</td>
                  <td className="text-right font-mono text-slate-500">{fmt(it.marketing_alloc)}</td>
                  <td className={`text-right font-mono font-semibold ${it.gross_profit >= 0 ? "text-emerald-700" : "text-red-600"}`}>{fmt(it.gross_profit)}</td>
                  <td className={`text-right font-mono font-semibold ${it.net_profit >= 0 ? "text-emerald-700" : "text-red-600"}`}>{fmt(it.net_profit)}</td>
                  <td className={`text-right font-mono ${it.net_margin_pct >= 0 ? "text-emerald-700" : "text-red-600"}`}>{it.net_margin_pct.toFixed(1)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </>
  );
}

// ═════════════════════════════════════════════════════════════
// TAB 3: Order details (per shop / month)
// ═════════════════════════════════════════════════════════════

function OrdersTab({
  codeId,
  carrier,
  productNames,
  variationIds,
  year,
  month,
}: {
  codeId: number | null;
  carrier: string | null;
  productNames: string[];
  variationIds: string[];
  year: number;
  month: number;
}) {
  const [filterDate, setFilterDate] = useState<string>("");
  const [filterStatus, setFilterStatus] = useState<string>("");
  const [onlyPartial, setOnlyPartial] = useState(false);

  const q = useQuery({
    queryKey: [
      "shop-monthly", codeId, carrier, year, month, productNames, variationIds,
    ],
    queryFn: async () =>
      (
        await api.get<MonthlyResult>("/reports/shop-monthly", {
          paramsSerializer: { indexes: null },
          params: {
            year,
            month,
            ...(codeId ? { product_code_id: codeId } : {}),
            ...(carrier ? { carrier } : {}),
            ...(productNames.length ? { product_name: productNames } : {}),
            ...(variationIds.length ? { variation_id: variationIds } : {}),
          },
        })
      ).data,
  });

  const orders = q.data?.order_details ?? [];

  // FULL list trạng thái POScake (không chỉ hiện có trong data)
  const allStatuses = useMemo(() => {
    // Đếm số lượng đơn per status để hiển thị bên cạnh label
    const cnt = new Map<number, number>();
    orders.forEach((o) => cnt.set(o.status, (cnt.get(o.status) ?? 0) + 1));
    // Sort theo thứ tự flow POScake logic
    const order = [0, 17, 1, 8, 9, 11, 12, 13, 20, 2, 3, 16, 4, 15, 5, 6, 7];
    const shown = new Set([...Object.keys(STATUS_MAP).map(Number), ...cnt.keys()]);
    return Array.from(shown)
      .sort((a, b) => order.indexOf(a) - order.indexOf(b))
      .map((s) => ({ code: s, count: cnt.get(s) ?? 0 }));
  }, [orders]);

  // Danh sách ngày có đơn — để populate select "Ngày"
  const availableDates = useMemo(() => {
    const set = new Set<string>();
    orders.forEach((o) => {
      if (o.date) set.add(o.date);
    });
    return Array.from(set).sort().reverse();
  }, [orders]);

  const filtered = useMemo(() => {
    return orders.filter((o) => {
      if (filterDate && o.date !== filterDate) return false;
      if (filterStatus !== "" && String(o.status) !== filterStatus) return false;
      if (onlyPartial && !o.is_partial) return false;
      return true;
    });
  }, [orders, filterDate, filterStatus, onlyPartial]);

  const totalCod = useMemo(
    () => filtered.reduce((s, o) => s + o.cod, 0),
    [filtered]
  );

  if (q.isLoading) {
    return <div className="text-slate-500 py-8 text-center">Đang tải...</div>;
  }
  if (!q.data) return null;

  return (
    <>
      {/* Filter bar */}
      <Card className="mb-3">
        <CardBody className="flex flex-wrap items-end gap-3">
          <div>
            <label className="label">Lọc theo ngày</label>
            <select
              value={filterDate}
              onChange={(e) => setFilterDate(e.target.value)}
              className="input w-44"
            >
              <option value="">— Cả tháng —</option>
              {availableDates.map((d) => (
                <option key={d} value={d}>
                  {new Date(d + "T00:00:00").toLocaleDateString("vi-VN")}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Trạng thái</label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="input w-56"
            >
              <option value="">— Tất cả trạng thái —</option>
              {allStatuses.map((s) => (
                <option key={s.code} value={s.code}>
                  {STATUS_MAP[s.code]?.label ?? `#${s.code}`}
                  {s.count > 0 ? ` (${s.count})` : ""}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Giao một phần</label>
            <button
              onClick={() => setOnlyPartial((v) => !v)}
              className={clsx(
                "input w-52 text-left",
                onlyPartial && "!border-amber-500 !text-amber-700 font-medium"
              )}
              title="Đơn khách chỉ trả tiền ship, phần hàng còn lại hoàn về"
            >
              {onlyPartial ? "✓ Chỉ đơn hoàn 1 phần" : "— Không lọc —"}
              {" ("}
              {orders.filter((o) => o.is_partial).length}
              {")"}
            </button>
          </div>
          {(filterDate || filterStatus || onlyPartial) && (
            <button
              onClick={() => {
                setFilterDate("");
                setFilterStatus("");
                setOnlyPartial(false);
              }}
              className="btn-ghost text-xs"
            >
              ✕ Xoá lọc
            </button>
          )}
          <div className="flex-1" />
          <div className="text-xs text-slate-500 pb-1.5">
            <span className="font-semibold text-slate-900">
              {filtered.length}
            </span>
            /{orders.length} đơn
            {filtered.length > 0 && (
              <>
                {" · "}
                <span className="font-semibold text-emerald-700">
                  {fmt(totalCod)}
                </span>
              </>
            )}
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title={`Chi tiết đơn hàng tháng ${String(month).padStart(2, "0")}/${year}`}
          subtitle={`${orders.length} đơn tổng · max 5000 mới nhất`}
        />
        <div className="overflow-x-auto scrollbar-thin">
          <table className="table-modern">
            <thead>
              <tr>
                <th>Ngày</th>
                <th>Shop</th>
                <th>Đơn POS</th>
                <th>Mã vận đơn</th>
                <th>Khách</th>
                <th>Sản phẩm</th>
                <th className="text-right">COD</th>
                <th className="text-right">Ship</th>
                <th>Trạng thái</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={9} className="text-center py-8 text-slate-500">
                    Không có đơn khớp bộ lọc
                  </td>
                </tr>
              )}
              {filtered.map((o) => {
                const st = STATUS_MAP[o.status] || {
                  label: o.status_name || `#${o.status}`,
                  tone: "badge-neutral",
                };
                return (
                  <tr key={o.pos_order_id}>
                    <td className="font-mono text-xs">
                      {o.date
                        ? new Date(o.date + "T00:00:00").toLocaleDateString(
                            "vi-VN"
                          )
                        : "—"}
                    </td>
                    <td className="text-xs text-slate-600">{o.shop_name}</td>
                    <td className="font-mono text-xs">{o.pos_order_id}</td>
                    <td className="font-mono text-xs">
                      {o.partner_extend_code || "—"}
                      {o.partner_name && (
                        <div className="text-[10px] text-slate-500">
                          {o.partner_name}
                        </div>
                      )}
                    </td>
                    <td className="text-xs">{o.customer || "—"}</td>
                    <td
                      className="text-xs max-w-xs truncate"
                      title={o.items || ""}
                    >
                      {o.items || "—"}
                    </td>
                    <td className="text-right font-mono font-semibold">
                      {o.is_partial ? (
                        <span
                          className="text-orange-600"
                          title={`Giao một phần: tính ${fmt(
                            o.cod
                          )} tiền ship thay cho COD gốc ${fmt(o.cod_gross)}`}
                        >
                          {fmt(o.cod)}
                          <span className="ml-1 text-[10px] text-slate-400 line-through font-normal">
                            {fmt(o.cod_gross)}
                          </span>
                        </span>
                      ) : (
                        fmt(o.cod)
                      )}
                    </td>
                    <td className="text-right font-mono text-slate-500">
                      {o.partner_fee > 0 ? fmt(o.partner_fee) : "—"}
                    </td>
                    <td>
                      <div className="flex items-center gap-1 flex-wrap">
                        <span className={st.tone}>{st.label}</span>
                        {o.is_partial && (
                          <span
                            className="badge-warning"
                            title="Giao một phần: khách trả tiền ship, phần hàng còn lại hoàn về bằng vận đơn con"
                          >
                            Hoàn 1 phần
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Hiệu suất nhân viên — số đơn mỗi người xác nhận, theo ngày + giờ.   */
/* Mốc = lần đơn chuyển sang trạng thái "Đã xác nhận" (POScake status 1). */
/* ------------------------------------------------------------------ */

function StaffTab({
  dateFrom,
  dateTo,
}: {
  dateFrom: string;
  dateTo: string;
}) {
  const q = useQuery({
    queryKey: ["staff-performance", dateFrom, dateTo],
    queryFn: async () =>
      (
        await api.get<StaffResult>("/reports/staff-performance", {
          params: { date_from: dateFrom, date_to: dateTo },
        })
      ).data,
  });

  if (q.isLoading)
    return <div className="text-slate-500 py-8 text-center">Đang tải…</div>;
  if (q.isError)
    return (
      <div className="text-red-600 py-8 text-center">
        Lỗi tải dữ liệu hiệu suất nhân viên.
      </div>
    );

  const d = q.data!;
  const { totals, staff, staff_names, days, hours } = d;
  const maxHour = Math.max(1, ...hours.map((h) => h.n));

  if (totals.n_confirmed === 0)
    return (
      <Card>
        <CardBody>
          <div className="text-center py-10 text-slate-500">
            Không có đơn nào được xác nhận trong khoảng này.
          </div>
        </CardBody>
      </Card>
    );

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard
          label="Đơn đã xác nhận"
          value={fmtNumOnly(totals.n_confirmed)}
          hint={`${dateFrom} → ${dateTo}`}
          icon={UserCheck}
          tone="green"
        />
        <KpiCard
          label="Nhân viên"
          value={fmtNumOnly(totals.n_staff)}
          hint="có xác nhận đơn trong kỳ"
          icon={Store}
        />
        <KpiCard
          label="TB mỗi ngày"
          value={fmtNumOnly(totals.avg_per_day)}
          hint={`${totals.n_days} ngày có đơn`}
          icon={CalendarDays}
        />
        <KpiCard
          label="Giờ cao điểm"
          value={`${hours.reduce((a, b) => (b.n > a.n ? b : a)).hour}h`}
          hint="toàn kỳ"
          icon={TrendingUp}
          tone="amber"
        />
      </div>

      {/* Theo nhân viên */}
      <Card>
        <CardHeader
          title="Theo nhân viên"
          subtitle="Xếp theo số đơn xác nhận. Khung giờ = sớm nhất → muộn nhất trong kỳ."
        />
        <CardBody>
          <div className="overflow-x-auto scrollbar-thin">
            <table className="table-modern">
              <thead>
                <tr>
                  <th className="min-w-[180px]">Nhân viên</th>
                  <th className="text-right">Số đơn</th>
                  <th className="text-right">Tỷ trọng</th>
                  <th className="text-right">Ngày làm</th>
                  <th className="text-right">TB/ngày</th>
                  <th className="text-center">Khung giờ</th>
                  <th className="text-center">Cao điểm</th>
                  <th className="min-w-[220px]">Phân bố theo giờ (0–23h)</th>
                </tr>
              </thead>
              <tbody>
                {staff.map((s) => {
                  const share = s.n_confirmed / totals.n_confirmed;
                  const mx = Math.max(1, ...s.by_hour);
                  return (
                    <tr key={s.editor_id ?? s.name}>
                      <td className="font-medium">{s.name}</td>
                      <td className="text-right font-mono font-semibold">
                        {fmtNumOnly(s.n_confirmed)}
                      </td>
                      <td className="text-right font-mono text-slate-500">
                        {(share * 100).toFixed(1)}%
                      </td>
                      <td className="text-right font-mono">{s.n_days_active}</td>
                      <td className="text-right font-mono font-semibold">
                        {s.avg_per_active_day}
                      </td>
                      <td className="text-center font-mono text-xs text-slate-600">
                        {s.earliest_time}–{s.latest_time}
                      </td>
                      <td className="text-center font-mono text-xs">
                        {s.peak_hour !== null ? `${s.peak_hour}h` : "—"}
                      </td>
                      <td>
                        <div className="flex items-end gap-[1px] h-7">
                          {s.by_hour.map((n, i) => (
                            <div
                              key={i}
                              title={`${i}h: ${n} đơn`}
                              className={clsx(
                                "flex-1 rounded-t-sm",
                                n > 0 ? "bg-brand-500" : "bg-slate-100"
                              )}
                              style={{
                                height: n > 0 ? `${(n / mx) * 100}%` : "2px",
                              }}
                            />
                          ))}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardBody>
      </Card>

      {/* Ma trận ngày × nhân viên */}
      <Card>
        <CardHeader
          title="Theo ngày"
          subtitle="Số đơn mỗi nhân viên xác nhận trong từng ngày."
        />
        <CardBody>
          <div className="overflow-x-auto scrollbar-thin">
            <table className="table-modern">
              <thead>
                <tr>
                  <th className="min-w-[110px]">Ngày</th>
                  {staff_names.map((n) => (
                    <th key={n} className="text-right min-w-[110px]">
                      {n}
                    </th>
                  ))}
                  <th className="text-right">Tổng</th>
                </tr>
              </thead>
              <tbody>
                {days.map((row) => (
                  <tr key={row.date}>
                    <td className="font-medium">{row.date}</td>
                    {staff_names.map((n) => {
                      const v = row.per_staff[n] ?? 0;
                      return (
                        <td
                          key={n}
                          className={clsx(
                            "text-right font-mono",
                            v === 0 && "text-slate-300"
                          )}
                        >
                          {v === 0 ? "—" : fmtNumOnly(v)}
                        </td>
                      );
                    })}
                    <td className="text-right font-mono font-semibold">
                      {fmtNumOnly(row.total)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="font-semibold border-t-2 border-slate-300">
                  <td>TỔNG</td>
                  {staff_names.map((n) => (
                    <td key={n} className="text-right font-mono">
                      {fmtNumOnly(
                        days.reduce((a, r) => a + (r.per_staff[n] ?? 0), 0)
                      )}
                    </td>
                  ))}
                  <td className="text-right font-mono">
                    {fmtNumOnly(totals.n_confirmed)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </CardBody>
      </Card>

      {/* Phân bố theo giờ */}
      <Card>
        <CardHeader
          title="Phân bố theo giờ"
          subtitle="Toàn bộ nhân viên cộng lại — giờ nào đơn được xác nhận nhiều nhất."
        />
        <CardBody>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={hours}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="hour"
                tickFormatter={(h) => `${h}h`}
                fontSize={11}
              />
              <YAxis fontSize={11} allowDecimals={false} />
              <Tooltip
                formatter={(v: number) => [`${v} đơn`, "Đã xác nhận"]}
                labelFormatter={(h) => `${h}:00 – ${h}:59`}
              />
              <Bar dataKey="n" radius={[3, 3, 0, 0]}>
                {hours.map((h) => (
                  <Cell
                    key={h.hour}
                    fill={h.n === maxHour ? "#f59e0b" : "#6366f1"}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </CardBody>
      </Card>
    </div>
  );
}
